import React, { useEffect, useRef, useState } from 'react';
import { useMutation } from 'react-query';

import ConfirmModal from '../Common/Modal/ConfirmModal';
import BottomSheetPopup from '../Common/Popup/BottomSheetPopup';
import { ChevronDownBoldIcon } from '../Icon';
import ActualPriceModal from '../Menu/ActualPriceModal';
import apiManager from '@/api/AxiosInstance';
import newApiManager from '@/api/NewAxiosInstance';
import { patchProductSalePrice } from '@/api/products/updateProducts';
import DiscontinuedModal, { DiscontinuedConfirmPayload } from '@/components/Products/DiscontinuedModal';
import { colors } from '@/const/colors';
import { COMMON_TOAST_ERROR } from '@/const/errorMessage';
import { COUNSELING, COUNSEL_COMPLTED, REQUEST } from '@/const/license';
import { BEFORE_SALE, COMPLETED, DELETED, ORIGIN_DATA_REGISTER, PAUSE, SALE, WAIT, WAITING } from '@/const/productStatus';
import { useToastContext } from '@/contexts/Common/ToastContext';
import { formatPrice } from '@/utils/common';

interface ProductStatusProps {
  status: EnumPresenter | undefined;
  selectBoxBoolean?: boolean;
  id?: number;
  path?: string;
  approvalStatus?: EnumPresenter | undefined;
  price?: number;
  setProductList?: React.Dispatch<React.SetStateAction<ProductDetailResponse[]>>;
  refetch?: () => void;
  onBottomSheetStateChange?: (isOpen: boolean) => void;
  detailStatusStyle?: boolean;
}

export default function ProductStatus({
  status,
  selectBoxBoolean = true,
  id,
  path = 'products',
  price,
  approvalStatus,
  setProductList,
  refetch,
  onBottomSheetStateChange,
  detailStatusStyle = false,
}: ProductStatusProps) {
  const [color, setColor] = useState<string | undefined>('gray');
  const [bg, setBg] = useState<string | undefined>('bg-gray-1');
  const [activeBg, setActiveBg] = useState<string | undefined>('bg-gray-1');
  const [bd, setBd] = useState<string | undefined>('border-gray-7');
  const [selectBox, setSelectBox] = useState<boolean>(false);
  const [isOpenBottomSheet, setIsOpenBottomSheet] = useState<boolean>(false);
  const [isClosing, setIsClosing] = useState<boolean>(false);
  const [isOpenConfirmModal, setIsOpenConfirmModal] = useState(false);
  const [isUsePriceInputPopup, setIsUsePriceInputPopup] = useState(false);
  const [isOpenDiscontinuedModal, setIsOpenDiscontinuedModal] = useState(false);
  const completeReasonRef = useRef<string>('');
  const [actualSalePrice, setActualSalePrice] = useState<number | undefined>();
  const [modalConfig, setModalConfig] = useState<ModalConfig>({
    title: '',
    content: '',
    leftButtonLabel: '',
    rightButtonLabel: '',
    onLeftButtonClick: () => {},
    onRightButtonClick: () => {},
  });
  const { showToast } = useToastContext();

  const productStatus = [
    { code: [ORIGIN_DATA_REGISTER, BEFORE_SALE], desc: '임시저장', color: 'text-gray-8', bg: 'bg-gray-1' },
    { code: [COMPLETED], desc: '판매완료', color: 'text-gray-6', bg: 'bg-gray-1' },
    { code: [COUNSEL_COMPLTED], desc: '상담완료', color: 'text-gray-6', bg: 'bg-gray-3' },
    {
      code: [SALE],
      desc: '판매중',
      color: 'text-blue-3',
      bg: 'bg-gray-1',
      bd: 'border-blue-3',
      activeBg: 'bg-primary-10',
    },
    {
      code: [PAUSE],
      desc: '판매중지',
      color: 'text-red',
      bg: 'bg-gray-1',
      bd: 'border-red',
      activeBg: 'bg-secondaryRed-1',
    },
    { code: [REQUEST], desc: '상담요청', color: 'text-green-0', bg: 'bg-gray-1' },
    { code: [COUNSELING], desc: '상담중', color: 'text-green-0', bg: 'bg-gray-1' },
    { code: [DELETED], desc: '삭제됨', color: 'text-gray-6', bg: 'bg-gray-1' },
    { code: [WAIT], desc: '판매대기', color: 'text-green-0', bg: 'bg-gray-1' },
  ];

  useEffect(() => {
    if (status) {
      const statusData = productStatus.find((item) => item.code.includes(status?.code));
      setColor(statusData?.color);
      setBg(statusData?.bg);
      setBd(statusData?.bd);
      setActiveBg(statusData?.activeBg);
    }
    // 현재 상태가 SALE 또는 PAUSE 일 때만 셀렉트 박스(드롭다운) 사용
    setSelectBox([SALE, PAUSE].includes(status?.code || ''));
  }, [status]);

  useEffect(() => {
    if (isClosing) {
      const timer = setTimeout(() => {
        setIsClosing(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isClosing]);

  const onClickSelectBox = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpenBottomSheet(true);
    onBottomSheetStateChange?.(true);
  };

  const closeBottomSheet = () => {
    setIsClosing(true);
    setIsOpenBottomSheet(false);
    onBottomSheetStateChange?.(false);
  };
  const closeConfrimModal = () => {
    setIsOpenConfirmModal(false);
  };

  const showActualSalePricePopup = () => {
    setIsOpenConfirmModal(false);
    setIsUsePriceInputPopup(true);
  };

  const onClickChangeStatus = (selectedStatus: string) => {
    if (!id || !path) return;
    newApiManager
      .patch(`/api/v1/${path}/${id}`, { status: selectedStatus })
      .then((response) => {
        const responseData: ProductDetailResponse = response.data;
        if (setProductList) {
          setProductList((prevData) =>
            prevData.map((item) => {
              if (item.id === responseData.id) {
                return { ...item, status: responseData.status };
              }
              return item;
            }),
          );
        }
        if (refetch) {
          refetch();
        }
        setIsOpenBottomSheet(false);
        onBottomSheetStateChange?.(false);
        const toastMsg =
          selectedStatus === SALE
            ? '판매중으로 변경되었어요.'
            : selectedStatus === PAUSE
            ? '판매중지로 변경되었어요.'
            : selectedStatus === COMPLETED
            ? '판매완료로 변경되었어요.'
            : '상태가 변경되었어요.';
        showToast(toastMsg, 'success', 'bottom');
      })
      .catch(() => {
        showToast(COMMON_TOAST_ERROR, 'error', 'bottom');
      });
  };

  const onClickMenuItem = (selectedStatus: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (selectedStatus === COMPLETED) {
      showActualSalePricePopup();
      return;
    }
    if (selectedStatus === PAUSE && path === 'products') {
      // 바텀시트는 그대로 두고 모달만 띄움 → 스크롤 잠금은 바텀시트가 유지
      setIsOpenDiscontinuedModal(true);
      return;
    }
    onClickChangeStatus(selectedStatus);
    setIsOpenBottomSheet(false);
    onBottomSheetStateChange?.(false);
  };

  const patchActualPriceMutation = useMutation(
    (request: { id: number; actualSalePrice: number; completeReason: string; status?: string }) =>
      patchProductSalePrice(request),
    {
      onSuccess: (response) => {
        const responseData: ProductDetailResponse = response.data;
        if (responseData) {
          showToast(
            responseData.status?.code === COMPLETED ? '판매완료로 변경되었어요.' : '실제 판매 가격이 저장되었어요.',
            'success',
            'bottom',
          );
          if (setProductList) {
            setTimeout(() => {
              setProductList((prevData) =>
                prevData.map((item) => {
                  if (item.id === responseData.id) {
                    return {
                      ...item,
                      status: responseData.status,
                      actualSalePrice: responseData.actualSalePrice,
                    };
                  }
                  return item;
                }),
              );
            }, 1000);
          }
          refetch?.();
          setIsClosing(true);
          setIsOpenBottomSheet(false);
          onBottomSheetStateChange?.(false);
        }
      },
      onError: () => {
        showToast(COMMON_TOAST_ERROR, 'error', 'bottom');
      },
    },
  );

  const modifyProductActualSalePrice = () => {
    if (actualSalePrice == null) {
      return;
    }
    if (price != null && (price + 1000 < actualSalePrice || price - 1000 > actualSalePrice)) {
        setIsOpenConfirmModal(true);
        setModalConfig({
          content: (
            <>
              <div className="mx-8 font-medium sm280:mx-0">
                입력하신 실제 판매 금액과
                <br />
                기존 금액의 차이가 큽니다.
                <br />
                이대로 입력하시겠어요?
              </div>
              <div className="text-gray-7 text-[16px] sm280:text-[14px] leading-[19px] sm280:leading-[17px] mt-[24px]">
                <div>기존 금액 {formatPrice(String(price))}</div>
                <div>실제 판매 금액 {formatPrice(String(actualSalePrice))}</div>
              </div>
            </>
          ),
          leftButtonLabel: '다시 입력',
          rightButtonLabel: '이대로 입력',
          onLeftButtonClick: () => {
            closeConfrimModal();
            setIsUsePriceInputPopup(true);
          },
          onRightButtonClick: () => {
            updateActualSalePrice();
          },
        });
      } else {
        updateActualSalePrice();
      }
  };

  const updateActualSalePrice = () => {
    if (actualSalePrice != null && id != null) {
      closeConfrimModal();
      patchActualPriceMutation.mutate({
        id,
        actualSalePrice,
        completeReason: completeReasonRef.current,
        status: COMPLETED,
      });
    }
  };

  const statusMenus =
    status?.code === SALE
      ? [
          { code: COMPLETED, desc: '판매완료' },
          { code: PAUSE, desc: '판매중지' },
        ]
      : status?.code === PAUSE
      ? [{ code: SALE, desc: '판매중' }]
      : [];

  return (
    <>
      {status?.code !== WAIT && approvalStatus?.code !== WAITING && selectBox && selectBoxBoolean ? (
        <>
          <button
            className={`${
              detailStatusStyle ? `w-[120px] h-[36px] text-[16px] relative ` : `w-fit `
            }  border  text-center flex items-center ${activeBg} text-white justify-center gap-1 text-[14px] leading-[18px] rounded-[8px] font-semibold py-1 px-2 `}
            onClick={(e) => onClickSelectBox(e)}
          >
            {status?.desc}
            <ChevronDownBoldIcon
              className={`${detailStatusStyle ? 'absolute right-3' : ''}`}
              color={detailStatusStyle ? colors.gray[0] : colors.gray[0]}
              width={16}
              height={16}
              strikethroughThickness={10}
            ></ChevronDownBoldIcon>
          </button>

          <div onClick={(e) => e.stopPropagation()}>
            <BottomSheetPopup
              isOpen={isOpenBottomSheet}
              onClose={closeBottomSheet}
              canClose={() => !isUsePriceInputPopup && !isOpenConfirmModal}
              title="상태 변경"
            >
              <div className="p-4" onClick={(e) => e.stopPropagation()}>
                {statusMenus.map((item, index) => (
                  <div key={index} className="py-4 cursor-pointer" onClick={(e) => onClickMenuItem(item.code, e)}>
                    {item.desc}
                  </div>
                ))}
              </div>
            </BottomSheetPopup>
          </div>
        </>
      ) : (
        <span
          className={`text-[14px] leading-[18px] rounded-[8px] bg-gray-1 font-semibold py-1 px-2 ${bg}  ${
            approvalStatus?.code === WAITING ? 'text-green-0' : `${color}`
          }`}
        >
          {approvalStatus?.code === WAITING ? '승인 대기중' : status?.desc}
        </span>
      )}

      <ConfirmModal
        isOpen={isOpenConfirmModal}
        title={modalConfig.title}
        content={modalConfig.content}
        leftButtonLabel={modalConfig.leftButtonLabel}
        rightButtonLabel={modalConfig.rightButtonLabel}
        onLeftButtonClick={modalConfig.onLeftButtonClick}
        onRightButtonClick={modalConfig.onRightButtonClick}
      />
      <ActualPriceModal
        isShow={isUsePriceInputPopup}
        setIsShow={setIsUsePriceInputPopup}
        placeholder="판매 금액"
        price={price}
        onClickOk={(completeReason) => {
          completeReasonRef.current = completeReason;
          modifyProductActualSalePrice();
        }}
        inputValue={actualSalePrice}
        setInputValue={setActualSalePrice}
      />
      <DiscontinuedModal
        isOpen={isOpenDiscontinuedModal}
        onClose={() => setIsOpenDiscontinuedModal(false)}
        onConfirm={({ pauseReason }: DiscontinuedConfirmPayload) => {
          if (!id) return;
          newApiManager
            .patch(`/api/v1/products/${id}`, { pauseReason, status: PAUSE })
            .then((response) => {
              const responseData: ProductDetailResponse = response.data;
              if (setProductList && responseData) {
                setProductList((prevData) =>
                  prevData.map((item) =>
                    item.id === responseData.id ? { ...item, status: responseData.status } : item,
                  ),
                );
              }
              refetch?.();
              setIsOpenDiscontinuedModal(false);
              setIsOpenBottomSheet(false);
              onBottomSheetStateChange?.(false);
              showToast('판매중지로 변경되었어요.', 'success', 'bottom');
            })
            .catch(() => {
              showToast(COMMON_TOAST_ERROR, 'error', 'bottom');
            });
        }}
      />
    </>
  );
}
